import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Smartphone, Monitor, Loader2 } from "lucide-react";

/**
 * Settings → Users → Edit User: every device this user has been seen on —
 * the random device ID each phone / browser makes for itself (App.js),
 * with model, OS, browser, app version, first / last seen and last IP.
 * "Also used by" flags a device shared with (or handed on from) another user.
 * Read only; admin endpoint.
 */
const when = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return ""; }
};

export function UserDevicesSection({ userId }) {
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !userId) return;
    setDevices(null); setErr("");
    axios.get(`${API}/admin/users/${userId}/devices`, { withCredentials: true })
      .then((r) => setDevices(r.data?.devices || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load devices"));
  }, [open, userId]);

  if (!userId) return null;
  return (
    <div className="border-t border-slate-700 pt-4 mt-4">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 text-left">
        <Smartphone className="w-4 h-4 text-blue-400" />
        <span className="text-xs font-semibold flex-1">Devices</span>
        {devices && <span className="text-[10px] text-slate-400 mr-2">{devices.length}</span>}
        <span className="text-[10px] text-slate-500">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {err ? (
            <p className="text-xs text-red-400">{err}</p>
          ) : !devices ? (
            <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
          ) : devices.length === 0 ? (
            <p className="text-xs text-slate-500">No devices recorded yet — they're collected from now on, each time this user uses the app.</p>
          ) : (
            devices.map((d) => {
              const Icon = d.device_type === "desktop" ? Monitor : Smartphone;
              const what = [d.device_model, !d.device_model && d.os, d.browser].filter(Boolean).join(" · ");
              return (
                <div key={d.device_id} className="rounded-lg border border-slate-700 bg-slate-800/60 p-2.5">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 shrink-0 text-slate-400" />
                    <span className="text-xs font-semibold text-slate-100 flex-1 min-w-0 truncate">{what || "Unknown device"}</span>
                    {d.app_version && <span className="text-[10px] rounded bg-slate-700 px-1.5 py-0.5 text-slate-300">App v{d.app_version}</span>}
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-slate-400 break-all">{d.device_id}</div>
                  <div className="mt-1 grid grid-cols-2 gap-x-3 text-[10px] text-slate-400">
                    <span>First: {when(d.first_seen)}</span>
                    <span>Last: {when(d.last_seen)}</span>
                    {d.last_ip && <span className="font-mono">IP {d.last_ip}</span>}
                    {d.seen_count ? <span>{d.seen_count} actions</span> : null}
                  </div>
                  {d.also_used_by?.length > 0 && (
                    <div className="mt-1.5 text-[10px] font-semibold text-amber-400">Also used by: {d.also_used_by.join(", ")}</div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
